import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const DEFAULT_PREFS: CookiePreferences = { necessary: true, analytics: false, marketing: false };
const STORAGE_KEY = "nardy_cookie_consent";

function getSavedConsent(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState<CookiePreferences>(DEFAULT_PREFS);

  useEffect(() => {
    // Defer past initial paint to avoid blocking LCP/Speed Index
    const show = () => { if (!getSavedConsent()) setVisible(true); };
    const idle = (window as any).requestIdleCallback;
    const handle = idle ? idle(show, { timeout: 2500 }) : window.setTimeout(show, 1800);
    return () => {
      if (idle && (window as any).cancelIdleCallback) (window as any).cancelIdleCallback(handle);
      else window.clearTimeout(handle as number);
    };
  }, []);

  const accept = (overrides?: Partial<CookiePreferences>) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prefs, ...overrides }));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[9999] p-4 sm:p-6 pointer-events-none">
      <div className="max-w-2xl mx-auto bg-card border border-border rounded-xl shadow-2xl pointer-events-auto">
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3 mb-3">
            <Shield className="w-5 h-5 text-brand mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-display font-bold text-foreground text-sm">Cookie Preferences</h3>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                We use cookies to enhance your experience. Read our{" "}
                <Link to="/privacy-policy" className="text-brand underline hover:text-brand-light">Privacy Policy</Link>.
              </p>
            </div>
          </div>
          <button onClick={() => setShowDetails(!showDetails)} className="flex items-center gap-1 text-xs font-medium text-brand hover:text-brand-light transition-colors mb-4">
            {showDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {showDetails ? "Hide details" : "Customize cookies"}
          </button>
          {showDetails && (
            <div className="space-y-3 mb-5 border-t border-border pt-4">
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-semibold text-foreground">Necessary</p><p className="text-xs text-muted-foreground">Required for the site to function.</p></div>
                <Switch checked disabled className="opacity-60" />
              </div>
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-semibold text-foreground">Analytics</p><p className="text-xs text-muted-foreground">Help us understand site usage.</p></div>
                <Switch checked={prefs.analytics} onCheckedChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))} />
              </div>
              <div className="flex items-center justify-between">
                <div><p className="text-sm font-semibold text-foreground">Marketing</p><p className="text-xs text-muted-foreground">Used for relevant ads.</p></div>
                <Switch checked={prefs.marketing} onCheckedChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))} />
              </div>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            {showDetails ? (
              <>
                <Button onClick={() => accept()} className="flex-1 bg-gradient-brand text-primary-foreground font-bold hover:opacity-90">Save Preferences</Button>
                <Button onClick={() => accept({ analytics: true, marketing: true })} variant="outline" className="flex-1 border-brand/40 text-brand hover:bg-brand/10">Accept All</Button>
              </>
            ) : (
              <>
                <Button onClick={() => accept({ analytics: true, marketing: true })} className="flex-1 bg-gradient-brand text-primary-foreground font-bold hover:opacity-90">Accept All Cookies</Button>
                <Button onClick={() => accept({ analytics: false, marketing: false })} variant="outline" className="flex-1 border-border text-muted-foreground hover:bg-muted">Reject Non-Essential</Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
